import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, fmtINR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Plus, X, Trash, Check, MagnifyingGlass, ArrowClockwise,
  CheckCircle, XCircle, ClockCounterClockwise, DownloadSimple, PencilSimple, PaperPlaneTilt, NotePencil,
} from "@phosphor-icons/react";
import { stdCase } from "@/lib/stdCase";
import { PaymentEntryGrid } from "@/components/payment/PaymentEntryGrid";
import { PaymentReportBar, BLANK_TYPE } from "@/components/payment/PaymentReportBar";

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------
const fmtDate = (s) => {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleDateString("en-IN", {
      day: "2-digit", month: "short", year: "2-digit",
    });
  } catch { return s; }
};

const CHAIN_TONES = {
  approved:    "bg-emerald-950/50 text-emerald-300 border-emerald-800",
  rejected:    "bg-rose-950/50    text-rose-300    border-rose-800",
  in_progress: "bg-cyan-950/50    text-cyan-300    border-cyan-800",
  pending:     "bg-amber-950/50   text-amber-300   border-amber-800",
  none:        "bg-slate-800/50   text-slate-400   border-slate-700",
  draft:       "bg-violet-950/50  text-violet-200  border-violet-700",
};

const editable = (r) => r.chain_status === "draft"
  || (["pending", "in_progress"].includes(r.chain_status) && Number(r.current_level || 1) <= 1);

// ===========================================================================
// Main component
// ===========================================================================
const l1Cleared = (r) => r.chain_status === "approved" || r.chain_status === "draft" || Number(r.current_level || 0) > 1;

export default function PaymentApprovalsContent({ refreshKey }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [q, setQ] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [commentDraft, setCommentDraft] = useState({});
  const [masterTypes, setMasterTypes] = useState([]);
  const [loadTick, setLoadTick] = useState(0);
  const [selected, setSelected] = useState([]);
  const [editRows, setEditRows] = useState(null);

  useEffect(() => {
    api.get("/payment-requests/master-data")
      .then((r) => setMasterTypes(r.data?.payment_types || []))
      .catch(() => setMasterTypes([]));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/payment-requests", {
        params: statusFilter === "all" ? {} : { status: statusFilter },
      });
      setRows(r.data?.rows || []);
      setLoadTick((t) => t + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load payment requests");
    } finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) =>
      [r.vendor, r.payment_type, r.mode_of_payment, r.cost_center, r.item, r.po_number, r.cheque_no, r.narration,
       r.manual_po_ref, r.uploaded_by]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(s))
    );
  }, [rows, q]);

  const eligibleIds = useMemo(() => filtered.filter(l1Cleared).map((r) => r.id), [filtered]);

  const typeOptions = useMemo(() => {
    const set = new Set(masterTypes);
    let hasBlank = false;
    rows.forEach((r) => { if (r.payment_type) set.add(r.payment_type); else hasBlank = true; });
    const list = [...set].sort((a, b) => a.localeCompare(b));
    return hasBlank ? [...list, BLANK_TYPE] : list;
  }, [masterTypes, rows]);

  const totals = useMemo(() => {
    const t = { pending: 0, approved: 0, rejected: 0, draft: 0, amount_pending: 0 };
    rows.forEach((r) => {
      const st = r.chain_status || "pending";
      if (st === "draft") { t.draft += 1; if (r.last_rejection) t.rejected += 1; }
      else if (st === "approved") { t.approved += 1; }
      else if (st === "rejected") { t.rejected += 1; }
      else { t.pending += 1; t.amount_pending += Number(r.amount || 0); }
    });
    return t;
  }, [rows]);

  const doApprove = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/payment-requests/${id}/approve`, { comment: commentDraft[id] || "" });
      toast.success("Approved");
      setCommentDraft((c) => ({ ...c, [id]: "" }));
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Approve failed";
      const isPoBlock = e?.response?.status === 409 && String(detail).toLowerCase().includes("po ");
      if (isPoBlock) {
        toast.error(`⚠ ${detail}`, { duration: 12000 });
      } else {
        toast.error(detail);
      }
    } finally { setBusyId(null); }
  };

  const doReject = async (id) => {
    const reason = commentDraft[id] || window.prompt("Rejection reason?");
    if (!reason) { toast.error("Reason required to reject"); return; }
    setBusyId(id);
    try {
      const r = await api.post(`/payment-requests/${id}/reject`, { reason });
      toast.success(r.data?.message || "Rejected");
      setCommentDraft((c) => ({ ...c, [id]: "" }));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reject failed");
    } finally { setBusyId(null); }
  };

  const doSubmitDraft = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/payment-requests/${id}/submit`);
      toast.success("Submitted for approval");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submit failed");
    } finally { setBusyId(null); }
  };

  const drafts = useMemo(() => rows.filter((r) => r.chain_status === "draft"), [rows]);
  const openGrid = (recs) => { setEditRows(recs); setShowForm(true); };

  const doDelete = async (id) => {
    if (!window.confirm("Delete this payment request?")) return;
    try {
      await api.delete(`/payment-requests/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const doExport = async () => {
    try {
      // Use the current status filter — export what the user is currently
      // looking at. `axios` on `api` returns JSON by default, so we use a
      // raw fetch to preserve the binary XLSX stream.
      const res = await api.get(
        `/payment-requests/export.xlsx?status=${encodeURIComponent(statusFilter || "all")}`,
        { responseType: "blob" },
      );
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const today = new Date().toISOString().slice(0, 10);
      a.download = `payment-requests-${statusFilter || "all"}-${today}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      toast.success("Downloaded XLSX with full approval trail");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="payment-approvals-content">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-[11px]">
            <Kpi label="Pending" value={totals.pending} tone="amber" testId="pmt-approvals-kpi-pending" />
            <Kpi label="Approved" value={totals.approved} tone="emerald" testId="pmt-approvals-kpi-approved" />
            <Kpi label="Sent back" value={totals.rejected} tone="rose" testId="pmt-approvals-kpi-rejected" />
            <Kpi label="Draft" value={totals.draft} tone="violet" testId="pmt-approvals-kpi-draft" />
            <Kpi label="₹ Pending" value={fmtINR(totals.amount_pending)} tone="cyan" testId="pmt-approvals-kpi-amount" />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <MagnifyingGlass size={12} weight="bold"
              className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…"
              className="h-9 w-52 pl-7 bg-slate-950 border-slate-700 text-slate-100 text-[12px]"
              data-testid="pmt-approvals-search" />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-9 w-40 bg-slate-950 border-slate-700 text-slate-100 text-[12px]"
              data-testid="pmt-approvals-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-950 border-slate-700 text-slate-100">
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="in_progress">In progress</SelectItem>
              <SelectItem value="pending">Pending (no chain)</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={load}
            className="h-9 border-slate-700 text-slate-300 bg-slate-900 hover:bg-slate-800"
            data-testid="pmt-approvals-refresh">
            <ArrowClockwise size={12} weight="bold" className="mr-1" />Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={doExport}
            className="h-9 border-emerald-800 text-emerald-300 bg-emerald-950/40 hover:bg-emerald-900/60"
            data-testid="pmt-approvals-export-xlsx"
            title="Download every payment request with every level's approver + comment as an Excel file">
            <DownloadSimple size={12} weight="bold" className="mr-1" />Export XLSX
          </Button>
          {drafts.length > 0 && (
            <Button size="sm" variant="outline" onClick={() => openGrid(drafts)}
              className="h-9 border-violet-700 text-violet-200 bg-violet-950/40 hover:bg-violet-900/50"
              data-testid="pmt-approvals-open-drafts">
              <NotePencil size={12} weight="bold" className="mr-1" />Open Drafts ({drafts.length})
            </Button>
          )}
          <Button size="sm" onClick={() => openGrid(null)}
            className="h-9 bg-cyan-600 hover:bg-cyan-500 text-white font-semibold"
            data-testid="pmt-approvals-new">
            <Plus size={13} weight="bold" className="mr-1" />New Payment Approval
          </Button>
        </div>
      </div>

      <PaymentReportBar typeOptions={typeOptions} refreshKey={`${refreshKey}-${loadTick}`}
        selectedIds={selected} onClearSelection={() => setSelected([])} />

      {/* Table */}
      <div className="rounded-md border border-slate-700 overflow-hidden">
        <div className="overflow-x-auto max-h-[640px] overflow-y-auto">
          <table className="w-full text-[11px]" data-testid="pmt-approvals-table">
            <thead className="bg-slate-900 sticky top-0 z-10">
              <tr className="text-[10px] uppercase tracking-wider font-bold text-cyan-200">
                <th className="px-2 py-2 w-8">
                  <input type="checkbox" className="h-3.5 w-3.5 accent-cyan-400" title="Select all L1-approved"
                    data-testid="pmt-approvals-select-all"
                    checked={eligibleIds.length > 0 && eligibleIds.every((id) => selected.includes(id))}
                    onChange={(e) => setSelected(e.target.checked ? eligibleIds : [])} />
                </th>
                <th className="text-left px-3 py-2">Date</th>
                <th className="text-left px-3 py-2">Payment Type</th>
                <th className="text-left px-3 py-2">Mode</th>
                <th className="text-left px-3 py-2">Vendor / Payee</th>
                <th className="text-left px-3 py-2">Cost Centre</th>
                <th className="text-left px-3 py-2">PO # / Ref</th>
                <th className="text-left px-3 py-2">Item / Purpose</th>
                <th className="text-right px-3 py-2">Amount</th>
                <th className="text-right px-3 py-2">Last Paid Amt</th>
                <th className="text-left px-3 py-2">Last Paid Date</th>
                <th className="text-left px-3 py-2">Cheque No</th>
                <th className="text-left px-3 py-2">Narration</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={15} className="text-center py-10 text-slate-500">Loading…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={15} className="text-center py-10 text-slate-500">
                  {q ? "No payment requests match your search." : "No payment requests yet. Click \"New Payment Approval\" to create one."}
                </td></tr>
              )}
              {!loading && filtered.map((r) => {
                const st = r.chain_status || "pending";
                const canAct = st !== "approved" && st !== "rejected" && st !== "draft";
                return (
                  <tr key={r.id} className="border-t border-slate-800 hover:bg-slate-900/40 align-top"
                    data-testid={`pmt-approvals-row-${r.id}`}>
                    <td className="px-2 py-2">
                      <input type="checkbox" className="h-3.5 w-3.5 accent-cyan-400 disabled:opacity-20"
                        disabled={!l1Cleared(r)} title={l1Cleared(r) ? "Select for download" : "Available after L1 approval (or while a draft)"}
                        data-testid={`pmt-approvals-select-${r.id}`}
                        checked={selected.includes(r.id)}
                        onChange={(e) => setSelected((s) => (e.target.checked ? [...s, r.id] : s.filter((x) => x !== r.id)))} />
                    </td>
                    <td className="px-3 py-2 font-mono text-slate-300 whitespace-nowrap">{fmtDate(r.date)}</td>
                    <td className="px-3 py-2 text-slate-300">{stdCase(r.payment_type) || "—"}</td>
                    <td className="px-3 py-2 text-slate-300 text-[10.5px]" data-testid={`pmt-approvals-mode-${r.id}`}>
                      {r.mode_of_payment ? (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-slate-800 border border-slate-700 text-cyan-200 tracking-wider text-[9.5px] font-bold">
                          {r.mode_of_payment}
                        </span>
                      ) : "—"}
                    </td>
                    <td className="px-3 py-2 font-semibold text-slate-100 min-w-[140px]">{stdCase(r.vendor)}</td>
                    <td className="px-3 py-2 text-cyan-200 text-[10.5px]">{stdCase(r.cost_center) || "—"}</td>
                    <td className="px-3 py-2 text-slate-400 font-mono text-[10.5px]">
                      {r.no_po_flag && (
                        <span className="inline-flex px-1.5 py-0.5 rounded bg-amber-950/60 border border-amber-800 text-amber-300 text-[9px] uppercase tracking-wider">No PO</span>
                      )}
                      <div className="mt-0.5">{r.po_number || r.manual_po_ref || "—"}</div>
                    </td>
                    <td className="px-3 py-2 text-slate-300 min-w-[160px] max-w-[240px] whitespace-normal break-words">{stdCase(r.item) || "—"}</td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums font-bold text-slate-100">{fmtINR(r.amount)}</td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums text-slate-400">{r.last_amount_paid ? fmtINR(r.last_amount_paid) : "—"}</td>
                    <td className="px-3 py-2 font-mono text-slate-400 whitespace-nowrap">{r.last_payment_date ? fmtDate(r.last_payment_date) : "—"}</td>
                    <td className="px-3 py-2 font-mono text-amber-200" data-testid={`pmt-approvals-cheque-${r.id}`}>{r.cheque_no || "—"}</td>
                    <td className="px-3 py-2 text-slate-400 min-w-[180px] max-w-[260px] whitespace-normal break-words" data-testid={`pmt-approvals-narration-${r.id}`}>{stdCase(r.narration) || "—"}</td>
                    <td className="px-3 py-2">
                      <StatusPill status={st} r={r} />
                      {r.pending_role_label && canAct && (
                        <div className="text-[9px] text-slate-500 mt-0.5">at {r.pending_role_label}</div>
                      )}
                      {canAct && Array.isArray(r.pending_approver_emails) && r.pending_approver_emails.length > 0 && (
                        <div className="text-[9px] text-cyan-400/80 mt-0.5" data-testid={`pmt-approvals-approver-emails-${r.id}`}>
                          {r.pending_approver_emails.join(", ")}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 min-w-[280px]">
                      {st === "draft" ? (
                        <div className="space-y-1.5">
                        {r.last_rejection && (
                          <div className="text-[10px] leading-snug text-rose-300 bg-rose-950/40 border border-rose-900 rounded px-2 py-1 whitespace-normal break-words max-w-[260px]"
                            data-testid={`pmt-approvals-rejection-${r.id}`}>
                            ✕ Rejected at L{r.last_rejection.level} by {String(r.last_rejection.rejected_by || "").split("@")[0]}
                            {r.last_rejection.reason && <span className="italic text-rose-200/80">: &ldquo;{r.last_rejection.reason}&rdquo;</span>}
                          </div>
                        )}
                        <div className="flex items-center gap-1.5">
                          <Button size="sm" variant="outline" onClick={() => openGrid([r])}
                            className="h-7 border-slate-700 text-slate-200 bg-slate-900 hover:bg-slate-800 text-[10.5px]"
                            data-testid={`pmt-approvals-edit-${r.id}`}>
                            <PencilSimple size={11} weight="bold" className="mr-1" />Edit
                          </Button>
                          <Button size="sm" disabled={busyId === r.id} onClick={() => doSubmitDraft(r.id)}
                            className="h-7 bg-cyan-700 hover:bg-cyan-600 text-white text-[10.5px] font-semibold"
                            data-testid={`pmt-approvals-submit-${r.id}`}>
                            <PaperPlaneTilt size={11} weight="bold" className="mr-1" />Submit
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => doDelete(r.id)}
                            className="h-7 text-slate-500 hover:text-rose-300 text-[10.5px]"
                            data-testid={`pmt-approvals-delete-${r.id}`}>
                            <Trash size={11} weight="bold" />
                          </Button>
                        </div>
                        </div>
                      ) : canAct ? (
                        <div className="space-y-1.5">
                          {/* Chain + prior-approver comments (visible to the
                              current approver so context flows through the
                              chain — this is what fixes the "comments saved
                              but not reflecting" bug). */}
                          <PaymentApprovalChain r={r} />
                          <Textarea
                            value={commentDraft[r.id] || ""}
                            onChange={(e) => setCommentDraft((c) => ({ ...c, [r.id]: e.target.value }))}
                            placeholder="Comment / reason (required on reject)"
                            className="h-14 text-[10.5px] bg-slate-950 border-slate-700 text-slate-100 resize-none"
                            data-testid={`pmt-approvals-comment-${r.id}`}
                          />
                          <div className="flex items-center gap-1.5">
                            <Button size="sm" disabled={busyId === r.id} onClick={() => doApprove(r.id)}
                              className="h-7 bg-emerald-700 hover:bg-emerald-600 text-white text-[10.5px] font-semibold"
                              data-testid={`pmt-approvals-approve-${r.id}`}>
                              <Check size={11} weight="bold" className="mr-1" />Approve
                            </Button>
                            <Button size="sm" variant="outline" disabled={busyId === r.id} onClick={() => doReject(r.id)}
                              className="h-7 border-rose-800 text-rose-300 bg-rose-950/40 hover:bg-rose-900/60 text-[10.5px] font-semibold"
                              data-testid={`pmt-approvals-reject-${r.id}`}>
                              <X size={11} weight="bold" className="mr-1" />Reject
                            </Button>
                            {editable(r) && (
                              <Button size="sm" variant="outline" onClick={() => openGrid([r])}
                                className="h-7 border-slate-700 text-slate-200 bg-slate-900 hover:bg-slate-800 text-[10.5px]"
                                data-testid={`pmt-approvals-edit-${r.id}`} title="Editable until L1 approves">
                                <PencilSimple size={11} weight="bold" />
                              </Button>
                            )}
                            <Button size="sm" variant="ghost" onClick={() => doDelete(r.id)}
                              className="h-7 text-slate-500 hover:text-rose-300 text-[10.5px]"
                              data-testid={`pmt-approvals-delete-${r.id}`}>
                              <Trash size={11} weight="bold" />
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-1.5 text-[10px] text-slate-500">
                          <PaymentApprovalChain r={r} />
                          <Button size="sm" variant="ghost" onClick={() => doDelete(r.id)}
                            className="h-6 text-slate-500 hover:text-rose-300 text-[10.5px]"
                            data-testid={`pmt-approvals-delete-${r.id}`}>
                            <Trash size={10} weight="bold" className="mr-1" />Delete
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Form dialog */}
      <PaymentEntryGrid open={showForm} editRows={editRows}
        onClose={() => { setShowForm(false); setEditRows(null); }} onSaved={load} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Approval-chain summary with comments — mirrors ApprovalChainStrip from
// the PO tab. Renders one row per configured level with the actual actor,
// action (✓/✕/·) and their comment/reason on that step. This is the only
// way L2 sees L1's comment (and audit sees everything on approved/rejected).
// ---------------------------------------------------------------------------
function PaymentApprovalChain({ r }) {
  const taken = Array.isArray(r.approvals) ? r.approvals : [];
  const totalLevels = r.total_levels || 0;
  if (!totalLevels && taken.length === 0) return null;

  const steps = [];
  const n = Math.max(totalLevels, taken.length);
  for (let lvl = 1; lvl <= n; lvl++) {
    // pick the LATEST audit entry for this level (survives reject cycles)
    let t = null;
    for (let i = taken.length - 1; i >= 0; i--) {
      if ((taken[i].level || 0) === lvl) { t = taken[i]; break; }
    }
    const action = t
      ? (t.rejected_by ? "reject" : t.approved_by ? "approve" : null)
      : null;
    steps.push({
      level: lvl,
      role:  t?.role_label || t?.role_id || `L${lvl}`,
      action,
      at:    t?.approved_at || t?.rejected_at || null,
      by:    t?.approved_by || t?.rejected_by || null,
      comment: t?.comment || t?.reason || null,
    });
  }

  const isFinal = r.chain_status === "approved" || r.chain_status === "rejected";
  const curLvl = r.current_level || 1;

  return (
    <div className="space-y-1" data-testid={`pmt-approvals-chain-${r.id}`}>
      {/* Level pills */}
      <div className="flex items-center gap-0.5 flex-wrap">
        {steps.map((s, i) => {
          const tone = s.action === "approve"
            ? "bg-emerald-950/60 text-emerald-300 border-emerald-800"
            : s.action === "reject"
              ? "bg-rose-950/60 text-rose-300 border-rose-800"
              : (!isFinal && curLvl === s.level)
                ? "bg-cyan-950/60 text-cyan-300 border-cyan-700 ring-1 ring-cyan-800/60"
                : "bg-slate-800/60 text-slate-400 border-slate-700";
          const icon = s.action === "approve" ? "✓"
                     : s.action === "reject"  ? "✕"
                     : (!isFinal && curLvl === s.level) ? "•" : "·";
          const tip = `L${s.level}${s.action ? " · " + s.action : ""}${
            s.at ? " on " + new Date(s.at).toLocaleDateString("en-IN") : ""
          }${s.by ? " by " + s.by : ""}${s.comment ? "\n💬 " + s.comment : ""}`;
          return (
            <React.Fragment key={s.level}>
              <span
                className={`inline-flex items-center gap-0.5 px-1 py-0.5 rounded-sm border text-[9px] font-bold uppercase tracking-wider ${tone}`}
                title={tip}
                data-testid={`pmt-approvals-chain-pill-${r.id}-l${s.level}`}
              >
                <span>L{s.level}</span>
                <span>{icon}</span>
              </span>
              {i < steps.length - 1 && <span className="text-slate-500 text-[9px]">→</span>}
            </React.Fragment>
          );
        })}
      </div>

      {/* Comment audit trail — one line per level that has a comment. */}
      {steps.some((s) => s.comment) && (
        <div className="space-y-0.5" data-testid={`pmt-approvals-chain-comments-${r.id}`}>
          {steps.filter((s) => s.comment).map((s) => (
            <div
              key={`c-${s.level}`}
              className="text-[10px] leading-snug flex items-start gap-1"
              data-testid={`pmt-approvals-chain-comment-${r.id}-l${s.level}`}
            >
              <span className={`shrink-0 font-bold ${s.action === "reject" ? "text-rose-400" : "text-emerald-400"}`}>
                L{s.level} {s.action === "reject" ? "✕" : "✓"}
              </span>
              <span className="text-slate-400 italic break-words">
                &ldquo;{s.comment}&rdquo;
                {s.by && <span className="not-italic text-slate-600 ml-1">— {String(s.by).split("@")[0]}</span>}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Status pill with chain info
// ---------------------------------------------------------------------------
function StatusPill({ status, r }) {
  const tone = CHAIN_TONES[status] || CHAIN_TONES.pending;
  const Icon = status === "approved" ? CheckCircle : status === "rejected" ? XCircle : ClockCounterClockwise;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[9.5px] font-bold uppercase tracking-wider ${tone}`}
      title={status === "in_progress" && r.total_levels ? `L${r.current_level} of ${r.total_levels}` : status}>
      <Icon size={10} weight="bold" />
      {status === "in_progress" ? `L${r.current_level}/${r.total_levels}` : status.replace("_", " ")}
    </span>
  );
}

function Kpi({ label, value, tone, testId }) {
  const tones = {
    amber:   "border-amber-800   bg-amber-950/40   text-amber-200",
    emerald: "border-emerald-800 bg-emerald-950/40 text-emerald-200",
    rose:    "border-rose-800    bg-rose-950/40    text-rose-200",
    cyan:    "border-cyan-800    bg-cyan-950/40    text-cyan-200",
    violet:  "border-violet-700  bg-violet-950/40  text-violet-200",
  };
  return (
    <div className={`px-2.5 py-1 rounded border ${tones[tone]}`} data-testid={testId}>
      <span className="text-[9px] uppercase tracking-wider font-bold opacity-80 mr-1.5">{label}</span>
      <span className="font-mono font-bold tabular-nums">{value}</span>
    </div>
  );
}
